import { type ReactNode, useEffect, useRef, useState } from "react";

import type { ActionBlockExtraRow, ActionBlockField, ActionBlockGroup, ActionBlockMode, ActionBlockRow } from "~/shared/ai/action-block";

import { Anchor, Box, Flex, Text } from "~/ui";
import { ActionGroupQuestion } from "./ActionRowQuestion";
import { BLOCK, chipStyle } from "./action-block-tokens";

export type ActionBlockLayout = "wide" | "stacked";
export type ActionBlockDensity = "card" | "pane";

/** Below this container width the three-column row cannot hold a rate line; rows stack instead. */
const STACK_BELOW_PX = 640;
/** Below this width the subject and context columns take their compact (pane) widths. */
const COMPACT_BELOW_PX = 860;

/** The rendered width of the row list, so layout follows the container rather than the viewport. */
function useContainerWidth(): { ref: (node: HTMLDivElement | null) => void; width: number } {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = (node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof ResizeObserver === "undefined") {
      return;
    }
    setWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    observerRef.current = observer;
  };
  useEffect(() => () => observerRef.current?.disconnect(), []);
  return { ref, width };
}

const ArrowGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flex: "none" }}>
    <path d="M3 8h10M9 4l4 4-4 4" stroke={BLOCK.faint} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Amber belongs to the open question alone; a row that cannot save sits on the quiet ground. */
const rowBackground = (row: ActionBlockRow): string | undefined =>
  row.state === "unsupported" || row.state === "left_out" ? BLOCK.footer : undefined;

const subjectColor = (row: ActionBlockRow): string =>
  row.state === "unsupported" || row.state === "left_out" || row.state === "no_change" ? BLOCK.secondary : BLOCK.ink;

const TABULAR = { fontVariantNumeric: "tabular-nums" } as const;

/** Shared widths so old values, arrows and new values line up from row to row. */
const BEFORE_WIDTH = { wide: 104, compact: 88 } as const;

/**
 * One unlabeled comparison: the figure names itself. The first is the row's lead figure, the new value largest with the old
 * one before the arrow; any later one (a package price) reads smaller beneath it. Receipts show the new value alone.
 */
const ComparisonLine = ({
  field,
  mode,
  lead,
  stacked,
  compact,
}: {
  field: ActionBlockField;
  mode: ActionBlockMode;
  lead: boolean;
  stacked: boolean;
  compact: boolean;
}) => {
  const before = mode === "review" && field.before ? field.before : null;
  const beforeSlot = mode === "review" && !stacked;
  return (
    <Flex align="baseline" gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
      {(before || (beforeSlot && field.before !== undefined && field.before !== "")) && (
        <>
          <Text
            style={{
              ...TABULAR,
              fontSize: lead ? 14 : BLOCK.bodySize,
              color: BLOCK.secondary,
              whiteSpace: "nowrap",
              textAlign: "right",
              minWidth: stacked ? 0 : compact ? BEFORE_WIDTH.compact : BEFORE_WIDTH.wide,
              flex: "none",
            }}
          >
            {before ?? "Not recorded"}
          </Text>
          <ArrowGlyph />
        </>
      )}
      <Text
        style={{
          ...TABULAR,
          fontSize: lead ? 16 : BLOCK.bodySize,
          lineHeight: lead ? "24px" : "20px",
          fontWeight: lead ? 600 : 500,
          color: lead ? BLOCK.ink : BLOCK.secondary,
          whiteSpace: "nowrap",
        }}
      >
        {field.after}
      </Text>
      {mode === "review" && field.delta && <span style={chipStyle(field.delta.tone)}>{field.delta.label}</span>}
    </Flex>
  );
};

/**
 * One labeled value line. Review mode reads old → new with a change chip; receipt mode shows the saved value alone. A
 * create has no "before" and wears a New chip while it waits to be saved.
 */
const FieldLine = ({
  field,
  mode,
  stacked,
  compact,
}: {
  field: ActionBlockField;
  mode: ActionBlockMode;
  stacked: boolean;
  compact: boolean;
}) => {
  const labelWidth = stacked ? 90 : 96;
  if (field.block) {
    return (
      <Box>
        <Flex align="center" gap={8}>
          <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{field.label}</Text>
          {field.before === undefined && mode === "review" && <span style={chipStyle("new")}>New</span>}
        </Flex>
        <Text style={{ fontSize: BLOCK.bodySize, lineHeight: 1.5, color: BLOCK.body, whiteSpace: "pre-wrap" }}>{field.after}</Text>
        {field.before && mode === "review" && (
          <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary, whiteSpace: "pre-wrap" }}>was {field.before}</Text>
        )}
      </Box>
    );
  }
  const numeric = field.kind === "money" || field.kind === "quantity";
  const guardBefore = !numeric && !stacked;
  const valueStyle = {
    ...TABULAR,
    fontSize: BLOCK.bodySize,
    fontWeight: 600,
    color: BLOCK.ink,
    textAlign: numeric ? ("right" as const) : ("left" as const),
  };
  if (mode === "receipt") {
    return (
      <Flex align="center" gap={8} wrap="nowrap" style={{ fontSize: BLOCK.bodySize, minWidth: 0 }}>
        <Text style={{ width: labelWidth, flex: "none", color: BLOCK.secondary, fontSize: BLOCK.bodySize }}>{field.label}</Text>
        <Text style={{ ...valueStyle, minWidth: stacked ? 0 : 90 }}>{field.after}</Text>
      </Flex>
    );
  }
  return (
    <Flex align="center" gap={8} wrap="nowrap" style={{ fontSize: BLOCK.bodySize, minWidth: 0 }}>
      <Text style={{ width: labelWidth, flex: "none", color: BLOCK.secondary, fontSize: BLOCK.bodySize }}>{field.label}</Text>
      {field.before === undefined ? (
        <span style={chipStyle("new")}>New</span>
      ) : field.before === "" ? null : (
        <>
          <Text
            style={{
              ...TABULAR,
              fontSize: BLOCK.bodySize,
              color: BLOCK.secondary,
              textAlign: numeric ? "right" : "left",
              minWidth: guardBefore || stacked || compact ? 0 : 70,
              flex: stacked ? "1 1 auto" : guardBefore ? "0 1 auto" : "none",
              // A long still-inline "was" value (Incoterm) ellipsizes instead of pushing the arrow and
              // after-value out of the body track. Numeric values keep their fixed width.
              ...(guardBefore ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } : {}),
            }}
            title={guardBefore ? (field.before ?? undefined) : undefined}
          >
            {field.before ?? "Not recorded"}
          </Text>
          <ArrowGlyph />
        </>
      )}
      {numeric ? (
        <Text style={{ ...valueStyle, minWidth: stacked || compact ? 0 : 86, flex: "none" }}>{field.after}</Text>
      ) : (
        // Inline text stays inside the body column: it ellipsizes as a whole and carries the full
        // value in the native title.
        <Text
          style={{ ...valueStyle, flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          title={field.after}
        >
          {field.after}
        </Text>
      )}
      {field.delta && <span style={chipStyle(field.delta.tone)}>{field.delta.label}</span>}
    </Flex>
  );
};

export interface ActionBlockRowsProps {
  rows: ActionBlockRow[];
  mode: ActionBlockMode;
  layout: ActionBlockLayout;
  density?: ActionBlockDensity;
  /** Leading control for a row, for example an include checkbox. */
  renderLead?: (row: ActionBlockRow) => ReactNode;
  /** Controls a row needs before it can proceed: named choices, never a guessed default. */
  renderQuestion?: (row: ActionBlockRow) => ReactNode;
  /** Row actions, under the row's values. */
  renderActions?: (row: ActionBlockRow) => ReactNode;
  /** A route from the row's identity to its record, when the review's own destination cannot land on it. */
  subjectLink?: (row: ActionBlockRow) => { href: string; title?: string } | null;
}

/** The single renderer for governed-action rows across card, side pane, receipt, and phone list. */
export const ActionBlockRows = ({
  rows,
  mode,
  layout,
  density = "card",
  renderLead,
  renderQuestion,
  renderActions,
  subjectLink,
}: ActionBlockRowsProps) => {
  const { ref, width } = useContainerWidth();
  const stacked = layout === "stacked" || (width > 0 && width < STACK_BELOW_PX);
  const compact = density === "pane" || (width > 0 && width < COMPACT_BELOW_PX);
  const subjectWidth = compact ? 180 : 260;
  const padding = density === "pane" ? "12px 20px" : "12px 18px";
  return (
    <div ref={ref} role="list">
      {rows.map((row, index) => {
        const lead = renderLead?.(row);
        const question = renderQuestion?.(row);
        const actions = renderActions?.(row);
        const link = subjectLink?.(row) ?? null;
        const notes = row.notes ?? [];
        const subjectBlock = (
          <Flex direction="column" gap={3} style={{ minWidth: 0 }}>
            <Flex align="baseline" gap={8} wrap="wrap">
              {link ? (
                <Anchor
                  href={link.href}
                  title={link.title}
                  style={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: subjectColor(row) }}
                >
                  {row.subject}
                </Anchor>
              ) : (
                <Text style={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: subjectColor(row) }}>{row.subject}</Text>
              )}
              {/* A saved row's title says it saved; only a row that did not save as asked keeps its tag. */}
              {row.kindLabel && !(mode === "receipt" && row.state === "saved") && <span style={chipStyle("neutral")}>{row.kindLabel}</span>}
            </Flex>
            {row.secondary && <Text style={{ fontSize: BLOCK.bodySize, lineHeight: "20px", color: BLOCK.secondary }}>{row.secondary}</Text>}
          </Flex>
        );
        const body = (
          <Flex direction="column" gap={5} style={{ minWidth: 0 }}>
            {row.reason && (
              <Text style={{ fontSize: BLOCK.bodySize, color: row.state === "needs_input" ? BLOCK.body : BLOCK.secondary }}>
                {row.reason}
              </Text>
            )}
            {question}
            {row.fields.map((field, fieldIndex) =>
              field.label === "" ? (
                <ComparisonLine
                  key={field.key}
                  field={field}
                  mode={mode}
                  lead={fieldIndex === row.fields.findIndex(candidate => candidate.label === "")}
                  stacked={stacked}
                  compact={compact}
                />
              ) : (
                <FieldLine key={field.key} field={field} mode={mode} stacked={stacked} compact={compact} />
              ),
            )}
            {row.context && (
              <Flex direction="column" gap={2}>
                <Text style={{ fontSize: BLOCK.bodySize, color: BLOCK.secondary, whiteSpace: stacked ? "normal" : "nowrap" }}>
                  {row.context.primary}
                </Text>
                {row.context.secondary && (
                  <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary, whiteSpace: stacked ? "normal" : "nowrap" }}>
                    {row.context.secondary}
                  </Text>
                )}
              </Flex>
            )}
            {row.unchanged && <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{row.unchanged}</Text>}
            {notes.map((note, noteIndex) => (
              <Text
                key={`${row.key}-note-${noteIndex}`}
                style={{
                  fontSize: BLOCK.meta,
                  color: note.tone === "muted" ? BLOCK.secondary : BLOCK.body,
                  fontWeight: note.tone === "warn" ? 500 : 400,
                }}
              >
                {note.text}
              </Text>
            ))}
          </Flex>
        );
        const borderBottom = index < rows.length - 1 ? `1px solid ${BLOCK.divider}` : undefined;
        if (stacked) {
          return (
            <Box key={row.key} role="listitem" style={{ padding: "12px 16px", borderBottom, background: rowBackground(row) }}>
              <Flex gap={10} align="flex-start">
                {lead}
                <Flex direction="column" gap={6} style={{ minWidth: 0, flex: "1 1 auto" }}>
                  {subjectBlock}
                  {body}
                </Flex>
                {actions && (
                  <Flex gap={14} style={{ flex: "none" }}>
                    {actions}
                  </Flex>
                )}
              </Flex>
            </Box>
          );
        }
        return (
          <Box
            key={row.key}
            role="listitem"
            style={{
              display: "grid",
              gridTemplateColumns: `${lead ? "auto " : ""}${subjectWidth}px minmax(0, 1fr)${actions ? " auto" : ""}`,
              gap: "0 16px",
              padding,
              borderBottom,
              alignItems: "start",
              background: rowBackground(row),
            }}
          >
            {lead}
            {subjectBlock}
            {body}
            {/* Row actions end the row's first line. */}
            {actions && (
              <Flex gap={14} justify="flex-end" style={{ alignSelf: "start" }}>
                {actions}
              </Flex>
            )}
          </Box>
        );
      })}
    </div>
  );
};

/**
 * A grouped review: each counted group shows its count and its rows; an uncounted group holds the
 * review's own questions, its rows and its lines; a collapsed group holds lines only. Groups come from
 * the server's saved evidence, so unchecking a ready row does not hide or move it.
 */
export function ActionBlockGroups<Item extends { row: ActionBlockRow }>({
  groups,
  extraRows,
  items,
  pageItems,
  renderItems,
  onClarify,
  canControl,
}: {
  groups: readonly ActionBlockGroup[];
  extraRows: readonly ActionBlockExtraRow[];
  /** Every loaded item, so a group's presence does not depend on the page. */
  items: readonly Item[];
  pageItems: readonly Item[];
  renderItems: (items: readonly Item[]) => ReactNode;
  onClarify?: (query: string) => Promise<void>;
  canControl: boolean;
}) {
  return (
    <>
      {[...groups]
        .sort((left, right) => left.order - right.order)
        .map(group => {
          const extra = extraRows.filter(row => row.group === group.key);
          const lines = extra.flatMap(row => ("text" in row ? [row] : []));
          if (group.collapsed) {
            return group.count ? (
              <details key={group.key} style={{ padding: "12px 20px", borderTop: `1px solid ${BLOCK.divider}` }}>
                <summary style={{ cursor: "pointer", fontSize: BLOCK.bodySize, fontWeight: 500 }}>
                  {group.label} · {group.count}
                </summary>
                {lines.map(line => (
                  <Text key={line.key} style={{ marginTop: 8, fontSize: BLOCK.meta }}>
                    {line.text}
                  </Text>
                ))}
              </details>
            ) : null;
          }
          const groupItems = pageItems.filter(item => item.row.group === group.key);
          if (group.count !== null) {
            return group.count ? (
              <Box key={group.key}>
                <Text component="h3" style={{ margin: 0, padding: "12px 20px 8px", fontSize: BLOCK.bodySize, fontWeight: 600 }}>
                  {group.label} · {group.count}
                </Text>
                {renderItems(groupItems)}
              </Box>
            ) : null;
          }
          if (extra.length === 0 && !items.some(item => item.row.group === group.key)) {
            return null;
          }
          return (
            <Box key={group.key} style={{ padding: "12px 20px" }}>
              <Text component="h3" style={{ margin: "0 0 8px", fontSize: BLOCK.bodySize, fontWeight: 600 }}>
                {group.label}
              </Text>
              {extra.map(row =>
                "question" in row ? (
                  <ActionGroupQuestion key={row.key} question={row.question} canControl={canControl} onClarify={onClarify} />
                ) : null,
              )}
              {renderItems(groupItems)}
              {lines.map(line => (
                <Text key={line.key} style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>
                  {line.text}
                </Text>
              ))}
            </Box>
          );
        })}
    </>
  );
}

/** Items a grouped review pages: rows of collapsed groups appear as their lines instead. */
export function pagedGroupItems<Item extends { row: ActionBlockRow }>(
  groups: readonly ActionBlockGroup[] | undefined,
  items: readonly Item[],
): Item[] {
  if (!groups) {
    return [...items];
  }
  const collapsed = new Set(groups.filter(group => group.collapsed).map(group => group.key));
  return items.filter(item => !item.row.group || !collapsed.has(item.row.group));
}
